import { createFileRoute } from "@tanstack/react-router";
import type { ClientBuilderClientAppHydrated } from "api-types/builderClientApps/builderClientAppsResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import ClientAppsTable from "lib/components/builders/developers/ClientAppsTable";
import CreateClientAppModal from "lib/components/builders/developers/CreateClientAppModal";
import { useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { useState } from "react";
import Button from "ui/buttons/Button";
import Loading from "ui/components/Loading";
import lazySuspense from "ui/hooks/lazySuspense";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import ErrorPage from "../../../../components/ErrorPage";
import withAuthentication from "../../../../components/withAuthentication";

const SwaggerDocumentation = lazySuspense(() => import("lib/components/builders/developers/SwaggerDocumentation"), {
  fallback: <Loading />,
});

export type DeveloperPageSection = "apps" | "documentation";

function DeveloperPage() {
  const { builderId } = useAppRouterParams();

  if (!builderId) {
    return null;
  }

  return <ReadyDeveloperPage builderId={builderId} />;
}

function ReadyDeveloperPage({ builderId }: { builderId: string }) {
  const [newClientAppOpen, setNewClientAppOpen] = useState(false);
  const { hasPermission } = useUserContext();
  const canEditClientApps = hasPermission("EDIT_CLIENT_APPS");
  const {
    loading,
    data,
    error,
    mutate: mutateClientApps,
  } = useFetch<ClientBuilderClientAppHydrated[]>(`${WEB_API_BASE_PATH}/builders/${builderId}/client-apps`, [builderId]);

  const visibleTabs: DeveloperPageSection[] = ["apps", "documentation"];
  const { tab, menuItems } = useTabbedMenu<DeveloperPageSection>(visibleTabs);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorPage statusCode={error.status} />;
  }

  assertIsNotUndefined(data);

  const headerActions = tab === "apps" && canEditClientApps && (
    <>
      <Button
        btnSize="large"
        onClick={() => {
          setNewClientAppOpen(true);
        }}
      >
        New App
      </Button>
      <CreateClientAppModal
        builderId={builderId}
        open={newClientAppOpen}
        setOpen={(isOpen) => {
          setNewClientAppOpen(isOpen);
        }}
        mutateApps={(app) => {
          void mutateClientApps([...data, app]);
        }}
      />
    </>
  );

  return (
    <BuilderPortalLayout
      header="Developer Portal"
      pageTitle="Developer Portal"
      menuItems={menuItems}
      headerActions={headerActions}
    >
      <div className="builder-portal-layout-content">
        {tab === "apps" && (
          <ClientAppsTable
            builderId={builderId}
            apps={data}
            mutateApps={(app) => {
              void mutateClientApps(data.filter((a) => a.id !== app.id));
            }}
          />
        )}
        {tab === "documentation" && <SwaggerDocumentation builderId={builderId} />}
      </div>
    </BuilderPortalLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/developers/")({
  component: withAuthentication(DeveloperPage, { permissions: ["VIEW_CLIENT_APPS"], products: ["api"] }),
});
